import React, { ReactNode } from 'react';
import { CompatRouter, Route, Routes } from 'react-router-dom-v5-compat';
import { BrowserRouter } from 'react-router-dom';

import { OpRawMenuItemType } from 'config/createRawMenuItems';
import { OpAppLayout } from 'new-components/OpAppLayout/OpAppLayout';
import type { OrgSwitcherProps } from 'new-components/OrgSwitcher/types';
import { SuirRemovedUiProvider } from 'routes/AppContainer/SuirRemovedUiContext';
import { RouteProvider } from 'context/RouteContext';

import { OpAppHomePageRedirect } from './components/OpAppHomePageRedirect';
import { OpPageNotFound } from './components/OpPageNotFound';
import { sharedRoutes } from './constants';
import { OpAccessDenied } from './components/OpAccessDenied';
import { OpAppPreChecks } from './components/OpAppPreChecks';

interface BasicRoute {
  path: string;
  allowedScopes?: string[]; // allowed scopes to go to this route
  requiresOrg?: boolean; // does this route require an org
  element: ReactNode; // element to render for page.
}

type HomeRoute = {
  isHomeRoute: true; // Has to be Home route
} & BasicRoute;

type NotHomeRoute = {
  isHomeRoute?: false; // This cannot be a Home route - there can only be one
} & BasicRoute;

// Allow only one route to be the home route, also require a home route
export type RoutesConfig = [HomeRoute, ...NotHomeRoute[]];

/**
 * @component
 * @description Component that wraps the mini apps to do shared checks on every page
 */
export const OpAppScaffold = ({
  appScopePrefix,
  basename,
  children,
  noLayoutRoutes,
  homePath,
  sidebarItems,
  title,
  orgSwitcherProps,
}: {
  appScopePrefix: string;
  basename: string;
  children: ReactNode;
  noLayoutRoutes?: ReactNode;
  homePath: string;
  sidebarItems: OpRawMenuItemType[];
  title: string;
  orgSwitcherProps?: OrgSwitcherProps;
}) => {
  return (
    <RouteProvider routes={sidebarItems}>
      <BrowserRouter basename={basename}>
        <CompatRouter>
          <OpAppPreChecks basename={basename} />
          <Routes>
            {/* Builtin routes */}
            <Route
              key={sharedRoutes.redirectToHome}
              path={sharedRoutes.redirectToHome}
              element={
                <OpAppHomePageRedirect
                  scopePrefix={appScopePrefix}
                  homePath={homePath}
                />
              }
            />

            {/* page-not-found route for manual navigate calls */}
            <Route
              key={sharedRoutes.pageNotFound}
              path={sharedRoutes.pageNotFound}
              element={<OpPageNotFound />}
            />

            <Route
              key={sharedRoutes.noAccess}
              path={sharedRoutes.noAccess}
              element={<OpAccessDenied />}
            />

            <Route element={children} />

            {/* handle non-existent routes going to the 404 page */}
            <Route path="*" element={<OpPageNotFound />} />

            {/* Layout with nav/sidebar */}
            {noLayoutRoutes && <Route path="/nl/">{noLayoutRoutes}</Route>}

            {/* Base layout with top nav and sidebar */}
            <Route
              key={'wrapper-/'}
              path="/"
              element={
                <SuirRemovedUiProvider>
                  {/**
                   * Temp context providers while SUIR refactored out and new
                   * nav is optional in CC
                   */}
                  {/* Im not convinced we want a loader here stacked with other loaders. */}
                  <OpAppLayout
                    homePath={homePath}
                    title={title}
                    items={sidebarItems}
                    orgSwitcherProps={orgSwitcherProps}
                  />
                </SuirRemovedUiProvider>
              }
            >
              {children}
            </Route>
          </Routes>
        </CompatRouter>
      </BrowserRouter>
    </RouteProvider>
  );
};
